import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, Loader2, Trash2, MessageSquare } from "lucide-react";
import { useCopilot } from "@/hooks/use-copilot";
import { useSpeechRecognition } from "@/hooks/use-speech-recognition";
import { MicButton } from "@/components/MicButton";
import { DEFAULT_SPEECH_LANG, type SpeechLangCode } from "@/lib/speech-languages";
import { useData } from "@/contexts/DataContext";
import { useAuth } from "@/hooks/use-auth";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { PlanTier } from "@/lib/planTypes";

const COPILOT_DAILY_LIMITS: Record<"pro" | "ultimate", number> = { pro: 25, ultimate: 50 };
const COPILOT_BASIC_LIFETIME = 5;
const COPILOT_ULTIMATE_OWNER_BONUS = 5;

function todayIST(): string {
  const d = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}

function isPlanActive(plan: any): boolean {
  if (!plan) return false;
  if (plan.tier === "basic") return false;
  if (!plan.expiresAt) return false;
  return new Date(plan.expiresAt).getTime() > Date.now();
}

interface CopilotWidgetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type SuggestionGroup = { label: string; items: string[] };

const SUGGESTION_GROUPS: SuggestionGroup[] = [
  {
    label: "Today's Sales",
    items: ["Show today's revenue", "How many bills today?", "Top 3 products today"],
  },
  {
    label: "Stock",
    items: ["Which items are low on stock?", "How many products below alert?"],
  },
  {
    label: "Expenses",
    items: ["Total expenses this month", "How many expense entries this month?"],
  },
  {
    label: "Settings",
    items: ["What's my printer width?", "Is GST enabled?", "Show my invoice layout"],
  },
];

const VOICE_LANG_KEY = "saathi.voiceLanguage";

function getStoredLang(): SpeechLangCode {
  try {
    const v = localStorage.getItem(VOICE_LANG_KEY);
    return (v as SpeechLangCode) || DEFAULT_SPEECH_LANG;
  } catch {
    return DEFAULT_SPEECH_LANG;
  }
}

// Localized greeting templates per supported language code.
// {name} placeholder gets replaced with ownerName or businessName.
const GREETINGS: Record<SpeechLangCode, { withName: string; noName: string }> = {
  "en-IN": { withName: "Namaste {name}! How can I help?", noName: "Namaste! How can I help?" },
  "hi-IN": { withName: "नमस्ते {name} जी! मैं कैसे मदद करूँ?", noName: "नमस्ते! मैं कैसे मदद करूँ?" },
  "mr-IN": { withName: "नमस्कार {name}! मी कशी मदत करू?", noName: "नमस्कार! मी कशी मदत करू?" },
  "bn-IN": { withName: "নমস্কার {name}! আমি কীভাবে সাহায্য করতে পারি?", noName: "নমস্কার! আমি কীভাবে সাহায্য করতে পারি?" },
  "ta-IN": { withName: "வணக்கம் {name}! நான் எப்படி உதவலாம்?", noName: "வணக்கம்! நான் எப்படி உதவலாம்?" },
  "te-IN": { withName: "నమస్కారం {name}! నేను ఎలా సహాయం చేయగలను?", noName: "నమస్కారం! నేను ఎలా సహాయం చేయగలను?" },
  "kn-IN": { withName: "ನಮಸ್ಕಾರ {name}! ನಾನು ಹೇಗೆ ಸಹಾಯ ಮಾಡಲಿ?", noName: "ನಮಸ್ಕಾರ! ನಾನು ಹೇಗೆ ಸಹಾಯ ಮಾಡಲಿ?" },
  "ml-IN": { withName: "നമസ്കാരം {name}! എങ്ങനെ സഹായിക്കാം?", noName: "നമസ്കാരം! എങ്ങനെ സഹായിക്കാം?" },
  "gu-IN": { withName: "નમસ્તે {name}! હું કેવી રીતે મદદ કરું?", noName: "નમસ્તે! હું કેવી રીતે મદદ કરું?" },
  "pa-IN": { withName: "ਸਤ ਸ੍ਰੀ ਅਕਾਲ {name}! ਮੈਂ ਕਿਵੇਂ ਮਦਦ ਕਰਾਂ?", noName: "ਸਤ ਸ੍ਰੀ ਅਕਾਲ! ਮੈਂ ਕਿਵੇਂ ਮਦਦ ਕਰਾਂ?" },
  "ur-IN": { withName: "السلام علیکم {name}! میں کیسے مدد کروں؟", noName: "السلام علیکم! میں کیسے مدد کروں؟" },
};

function getGreeting(lang: SpeechLangCode, name: string): string {
  const g = GREETINGS[lang] || GREETINGS["en-IN"];
  return name ? g.withName.replace("{name}", name) : g.noName;
}

export function CopilotWidget({ open, onOpenChange }: CopilotWidgetProps) {
  const { messages, pending, send, clear } = useCopilot();
  const { businessProfile, businessName, plan } = useData();
  const { user } = useAuth();
  const [input, setInput] = useState("");
  const [voiceSource, setVoiceSource] = useState(false);
  const [aiSettings, setAiSettings] = useState<{
    copilotUsedToday?: number;
    copilotLastDate?: string;
    copilotLifetimeUsed?: number;
  }>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const lang = getStoredLang();
  const speech = useSpeechRecognition({ lang });

  // Subscribe to user doc for aiSettings while widget open + after each send
  useEffect(() => {
    if (!open || !user) return;
    const unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      const s = (snap.data()?.settings?.aiSettings || {}) as typeof aiSettings;
      setAiSettings(s);
    });
    return () => unsub();
  }, [open, user]);

  // Resolve effective tier + remaining quota
  const tier: PlanTier = (plan?.tier || "basic") as PlanTier;
  const effectiveTier: PlanTier = tier !== "basic" && !isPlanActive(plan) ? "basic" : tier;
  const isOwner = true; // mobile = owner unless desktop staff session; safe default
  let usedDisplay: string;
  if (effectiveTier === "basic") {
    const used = aiSettings.copilotLifetimeUsed || 0;
    const remaining = Math.max(0, COPILOT_BASIC_LIFETIME - used);
    usedDisplay = `${remaining}/${COPILOT_BASIC_LIFETIME} free trial left`;
  } else {
    const limit =
      COPILOT_DAILY_LIMITS[effectiveTier as "pro" | "ultimate"] +
      (effectiveTier === "ultimate" && isOwner ? COPILOT_ULTIMATE_OWNER_BONUS : 0);
    const today = todayIST();
    const used = aiSettings.copilotLastDate === today ? aiSettings.copilotUsedToday || 0 : 0;
    const remaining = Math.max(0, limit - used);
    usedDisplay = `${remaining}/${limit} questions left today`;
  }

  const displayName = (businessProfile?.ownerName || businessName || "").trim();
  // Use first word only — feels personal, avoids long names breaking layout
  const firstName = displayName.split(/\s+/)[0] || "";

  // Rotate greeting through all 11 supported languages every 1.7s while empty
  const greetingLangs = Object.keys(GREETINGS) as SpeechLangCode[];
  const [greetIdx, setGreetIdx] = useState(0);
  useEffect(() => {
    if (!open || messages.length > 0) return;
    const id = setInterval(() => {
      setGreetIdx((i) => (i + 1) % greetingLangs.length);
    }, 1700);
    return () => clearInterval(id);
  }, [open, messages.length, greetingLangs.length]);
  const greeting = getGreeting(greetingLangs[greetIdx], firstName);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else if (speech.isRecording) {
      speech.stop();
    }
  }, [open]);

  useEffect(() => {
    if (speech.isRecording) {
      const merged = (speech.transcript + " " + speech.interim).trim();
      if (merged) setInput(merged);
    }
  }, [speech.transcript, speech.interim, speech.isRecording]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, pending]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || pending) return;
    const wasVoice = voiceSource;
    setInput("");
    setVoiceSource(false);
    await send(text, wasVoice ? "voice" : "text");
  };

  const toggleMic = () => {
    if (speech.isRecording) {
      speech.stop();
    } else {
      setVoiceSource(true);
      speech.start();
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-[88vh] p-0 rounded-t-[32px] flex flex-col bg-gradient-to-b from-[#FFFBF2] via-white to-white dark:from-[#1A1614] dark:via-[#15110F] dark:to-[#0F0D0B] border-0 shadow-[0_-30px_80px_-15px_rgba(139,26,26,0.45)] overflow-hidden"
      >
        {/* Top accent gradient line */}
        <div
          className="absolute top-0 left-0 right-0 h-[2px] z-10"
          style={{
            background:
              "linear-gradient(90deg, transparent, #8B1A1A 20%, #C72525 50%, #8B1A1A 80%, transparent)",
          }}
        />
        {/* Drag handle */}
        <div className="flex justify-center pt-2.5 pb-1 shrink-0">
          <span className="w-10 h-1 rounded-full bg-slate-300/60 dark:bg-slate-700/60" />
        </div>
        {/* Header */}
        <div className="px-5 py-3 border-b border-[#8B1A1A]/10 dark:border-slate-800/60 flex items-center justify-between shrink-0 bg-gradient-to-r from-[#8B1A1A]/[0.06] via-transparent to-transparent backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <div className="relative w-11 h-11 shrink-0">
              {/* Conic gradient ring */}
              <span
                className="absolute -inset-[2px] rounded-2xl opacity-80"
                style={{
                  background:
                    "conic-gradient(from 0deg, #8B1A1A, #C72525, #FF6B35, #C72525, #8B1A1A)",
                  animation: "spin 5s linear infinite",
                }}
              />
              <div
                className="relative w-full h-full rounded-2xl flex items-center justify-center shadow-lg shadow-[#8B1A1A]/40 overflow-hidden"
                style={{
                  background:
                    "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 40%), linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                }}
              >
                <Sparkles className="w-5 h-5 text-white animate-[pulse_2.5s_ease-in-out_infinite]" strokeWidth={2.25} />
              </div>
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white shadow" />
            </div>
            <div>
              <SheetTitle className="font-black text-base tracking-tight text-left flex items-center gap-1.5">
                <span className="bg-gradient-to-r from-[#8B1A1A] to-[#C72525] bg-clip-text text-transparent">Saathi</span>
                <span className="text-[9px] font-black uppercase tracking-[0.25em] text-white bg-gradient-to-br from-[#8B1A1A] to-[#631212] px-1.5 py-0.5 rounded-md mt-0.5">AI</span>
              </SheetTitle>
              <p className="text-[10px] font-medium tracking-wide text-slate-500 dark:text-slate-400">
                Your shop companion
              </p>
            </div>
          </div>
          {messages.length > 0 && (
            <Button
              size="icon"
              variant="ghost"
              onClick={clear}
              className="h-8 w-8 rounded-full text-slate-400 hover:text-[#8B1A1A]"
              title="Clear conversation"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {messages.length === 0 && !pending && (
            <div className="flex flex-col items-center text-center">
              <p
                key={greetIdx}
                className="font-black tracking-tight mb-2 mt-3 animate-in fade-in slide-in-from-bottom-2 duration-700 leading-tight px-2"
                style={{
                  fontSize: "clamp(22px, 6.5vw, 30px)",
                  background: "linear-gradient(135deg, #8B1A1A 0%, #C72525 50%, #FF6B35 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {greeting}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-6 font-medium tracking-wide">
                Tap a suggestion or type
              </p>
              <div className="w-full space-y-4">
                {SUGGESTION_GROUPS.map((group) => (
                  <div key={group.label} className="text-left">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#8B1A1A] mb-2 px-1">
                      {group.label}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {group.items.map((s) => (
                        <button
                          key={s}
                          onClick={() => send(s, "text")}
                          className="text-left text-xs font-semibold px-3.5 py-2 rounded-2xl bg-white dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 hover:border-[#8B1A1A]/60 hover:bg-[#8B1A1A]/5 hover:text-[#8B1A1A] active:scale-95 transition-all text-slate-700 dark:text-slate-300 shadow-sm"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} animate-in fade-in slide-in-from-bottom-2 duration-300`}>
              {m.role === "assistant" && (
                <div className="relative w-6 h-6 shrink-0 mr-1.5 mt-1">
                  <span
                    className="absolute -inset-[1px] rounded-lg opacity-80"
                    style={{
                      background:
                        "conic-gradient(from 0deg, #8B1A1A, #C72525, #FF6B35, #C72525, #8B1A1A)",
                      animation: "spin 5s linear infinite",
                    }}
                  />
                  <div
                    className="relative w-full h-full rounded-lg flex items-center justify-center shadow-sm shadow-[#8B1A1A]/30 overflow-hidden"
                    style={{
                      background:
                        "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 40%), linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                    }}
                  >
                    <Sparkles className="w-3 h-3 text-white animate-[pulse_2.5s_ease-in-out_infinite]" strokeWidth={2.25} />
                  </div>
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-gradient-to-br from-[#8B1A1A] to-[#631212] text-white shadow-lg shadow-[#8B1A1A]/20 rounded-br-md"
                    : "bg-white dark:bg-slate-800/80 text-slate-800 dark:text-slate-100 border border-slate-200/60 dark:border-slate-700/60 shadow-sm rounded-bl-md backdrop-blur-sm"
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}

          {pending && (
            <div className="flex justify-start animate-in fade-in duration-200">
              <div className="relative w-6 h-6 shrink-0 mr-1.5 mt-1">
                <span
                  className="absolute -inset-[1px] rounded-lg opacity-80"
                  style={{
                    background:
                      "conic-gradient(from 0deg, #8B1A1A, #C72525, #FF6B35, #C72525, #8B1A1A)",
                    animation: "spin 5s linear infinite",
                  }}
                />
                <div
                  className="relative w-full h-full rounded-lg flex items-center justify-center shadow-sm shadow-[#8B1A1A]/30 overflow-hidden"
                  style={{
                    background:
                      "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 40%), linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                  }}
                >
                  <Sparkles className="w-3 h-3 text-white animate-pulse" strokeWidth={2.25} />
                </div>
              </div>
              <div className="rounded-2xl rounded-bl-md px-4 py-3 bg-white dark:bg-slate-800/80 border border-slate-200/60 dark:border-slate-700/60 flex items-center gap-1.5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-[#8B1A1A] animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#8B1A1A] animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#8B1A1A] animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="px-3 py-3 border-t border-slate-200/60 dark:border-slate-800/60 shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-white/80 dark:bg-[#0F0D0B]/80 backdrop-blur-md">
          <div className={`flex items-end gap-2 rounded-3xl p-2 transition-all ${speech.isRecording ? "bg-[#8B1A1A]/10 ring-2 ring-[#8B1A1A]/30" : "bg-slate-100/80 dark:bg-slate-800/60"}`}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={speech.isRecording ? "Listening..." : "Ask about your store..."}
              rows={1}
              maxLength={500}
              disabled={pending}
              className="flex-1 bg-transparent border-none outline-none resize-none text-sm px-3 py-2 max-h-32 placeholder:text-slate-400 dark:placeholder:text-slate-500 text-slate-800 dark:text-slate-100"
            />
            <MicButton
              isRecording={speech.isRecording}
              isSupported={speech.isSupported}
              disabled={pending}
              onClick={toggleMic}
            />
            <Button
              size="icon"
              onClick={handleSend}
              disabled={pending || !input.trim()}
              className="h-9 w-9 rounded-full bg-gradient-to-br from-[#8B1A1A] to-[#631212] hover:from-[#9F2020] hover:to-[#8B1A1A] text-white shrink-0 shadow-lg shadow-[#8B1A1A]/30 disabled:opacity-40 disabled:shadow-none"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          {speech.error && (
            <p className="text-[10px] text-red-500 text-center mt-1.5 font-medium">
              {speech.error}
            </p>
          )}
          <p className="text-[9px] text-slate-400 dark:text-slate-500 text-center mt-1.5 tracking-wider font-medium">
            {usedDisplay} · Powered by DailyHisaab
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
